import { gpHome } from '../app';
import {
    IAiCreditsPurchaseApi,
    AiCreditPurchaseOptions,
    AiCreditPurchaseConfirmation,
    AiCreditPurchasePool,
    AiCreditSavedCards,
    AiCreditsPurchaseServiceName,
} from './ai-credits-purchase-contract';
import { StripeInvoiceSummary } from '../../app-common/services/subscription.models';

import type * as ng from 'angular';

/** Wire shape of GET api/ai-credits/purchase-options (prices in cents). */
interface PurchaseOptionsDto {
    available: boolean;
    canPurchase: boolean;
    packs: {
        id: string;
        credits: number;
        priceCents: number;
        badge?: string;
        featured?: boolean;
    }[];
    pools?: AiCreditPurchasePool[];
}

/**
 * Wire shape of POST api/ai-credits/checkout. The owner fields are optional on the
 * server: absent means "the pool I'm billed to" (#724).
 */
interface CheckoutRequestDto {
    packId: string;
    consentToSavedCards: boolean;
    ownerType?: string;
    ownerId?: string;
}

/**
 * Real credit-purchase service (#76). Talks to the web role's Stripe-backed
 * endpoints; the post-redirect confirm is the step that actually posts the credits
 * to the ledger.
 */
class AiCreditsPurchaseService implements IAiCreditsPurchaseApi {

    static $inject = ['$http'];
    static serviceName = AiCreditsPurchaseServiceName;

    private readonly serviceBase = __gameplan_service_url || 'http://localhost/';

    constructor(private readonly $http: ng.IHttpService) { }

    getOptions(): ng.IPromise<AiCreditPurchaseOptions> {
        return this.$http
            .get<PurchaseOptionsDto>(this.serviceBase + 'api/ai-credits/purchase-options')
            .then(response => ({
                available: !!response.data.available,
                canPurchase: !!response.data.canPurchase,
                // The wire carries exact cents (promos may be non-round); the panel
                // renders whole dollars.
                packs: (response.data.packs || []).map(p => ({
                    id: p.id,
                    credits: p.credits,
                    priceUsd: p.priceCents / 100,
                    badge: p.badge || undefined,
                    featured: !!p.featured,
                })),
                // Passed through as the server ordered them (personal first, then teams
                // in join order): that order is the order spend falls through pools, so
                // re-sorting here would throw away what it explains. A server that
                // predates #724 sends none, which the panel reads as "no choice to make".
                pools: response.data.pools || [],
            }));
    }

    getSavedCards(): ng.IPromise<AiCreditSavedCards> {
        return this.$http
            .get<AiCreditSavedCards>(this.serviceBase + 'api/ai-credits/saved-cards')
            .then(response => ({
                cards: (response.data && response.data.cards) || [],
                consentNeeded: !!(response.data && response.data.consentNeeded),
            }));
    }

    startCheckout(
        packId: string,
        consentToSavedCards?: boolean,
        pool?: AiCreditPurchasePool | null,
    ): ng.IPromise<string> {
        const body: CheckoutRequestDto = { packId, consentToSavedCards: !!consentToSavedCards };

        // Only sent when a pool was chosen. Leaving the fields off entirely is what the
        // server reads as "the pool I'm billed to" (#724) — sending them empty would be
        // a half-named owner, which it refuses rather than guess at.
        if (pool) {
            body.ownerType = pool.ownerType;
            body.ownerId = pool.ownerId;
        }

        return this.$http
            .post<{ checkoutUrl: string }>(this.serviceBase + 'api/ai-credits/checkout', body)
            .then(response => response.data.checkoutUrl);
    }

    confirmCheckout(sessionId: string): ng.IPromise<AiCreditPurchaseConfirmation> {
        return this.$http
            .post<AiCreditPurchaseConfirmation>(this.serviceBase + 'api/ai-credits/confirm', { sessionId })
            .then(response => response.data);
    }

    getInvoices(): ng.IPromise<StripeInvoiceSummary[]> {
        return this.$http
            .get<StripeInvoiceSummary[]>(this.serviceBase + 'api/ai-credits/invoices')
            .then(response => response.data || []);
    }
}

gpHome.service(AiCreditsPurchaseService.serviceName, AiCreditsPurchaseService);
